import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PawPrint, Plus } from "lucide-react";
import { TaskCard } from "@/components/TaskCard";
import { PetAvatar } from "@/components/PetAvatar";
import { HouseholdCtx } from "@/components/AppShell";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const CACHE = "pl_today";

export default function Today() {
  const { user } = useAuth();
  const { household } = useContext(HouseholdCtx);
  const [data, setData] = useState(() => { try { return JSON.parse(localStorage.getItem(CACHE)); } catch (e) { return null; } });
  const [filter, setFilter] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/today");
      setData(d);
      localStorage.setItem(CACHE, JSON.stringify(d));
    } catch (e) { /* keep cached */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [load]);

  const done = async (task) => {
    setBusy(task.key);
    const optimistic = { log_id: "tmp", by: user.name, at: new Date().toISOString(), via: "member" };
    setData((d) => ({ ...d, tasks: d.tasks.map((t) => (t.key === task.key ? { ...t, done: optimistic } : t)) }));
    try {
      await api.post("/today/log", { schedule_id: task.schedule_id, time: task.time });
      toast.success(`${task.title} for ${task.pet_name}: logged!`);
      if (navigator.vibrate) navigator.vibrate(30);
    } catch (err) {
      toast[err.response?.status === 409 ? "warning" : "error"](errMsg(err));
    }
    setBusy(null);
    load();
  };

  const undo = async (task) => {
    try { await api.delete(`/today/log/${task.done.log_id}`); toast("Undone"); } catch (err) { toast.error(errMsg(err)); }
    load();
  };

  if (!data) return <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="pl-card h-20 animate-pulse" />)}</div>;

  const tasks = data.tasks.filter((t) => !filter || t.pet_id === filter);
  const todo = tasks.filter((t) => !t.done);
  const doneList = tasks.filter((t) => t.done);
  const pct = data.tasks.length ? Math.round((data.tasks.filter((t) => t.done).length / data.tasks.length) * 100) : 0;
  const dateLabel = new Date(`${data.date}T12:00:00`).toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });

  return (
    <div data-testid="today-page">
      <div className="flex items-end justify-between mb-4">
        <div>
          <p className="text-sm font-semibold text-slate-500">{dateLabel}{household ? ` · ${household.name}` : ""}</p>
          <h1 className="font-heading text-4xl font-extrabold">Today</h1>
        </div>
        <p className="font-heading text-3xl font-bold text-[#10B981]" data-testid="today-progress">{doneList.length}/{tasks.length}</p>
      </div>
      <div className="h-3 rounded-full bg-[#EFECE6] mb-5 overflow-hidden"><div className="h-full rounded-full bg-[#10B981] transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>

      {data.pets.length > 0 && (
        <div className="flex gap-3 overflow-x-auto no-scrollbar pb-3 -mx-4 px-4 mb-2">
          <button data-testid="today-filter-all" onClick={() => setFilter(null)} className={`shrink-0 flex flex-col items-center gap-1 text-xs font-semibold ${!filter ? "text-[#1E293B]" : "text-slate-400"}`}>
            <span className={`h-14 w-14 rounded-full grid place-items-center bg-white border-2 ${!filter ? "border-[#F59E0B]" : "border-transparent"}`}><PawPrint className="h-6 w-6" /></span>All
          </button>
          {data.pets.map((p) => (
            <button key={p.id} data-testid={`today-filter-${p.id}`} onClick={() => setFilter(filter === p.id ? null : p.id)} className={`shrink-0 flex flex-col items-center gap-1 text-xs font-semibold ${filter === p.id ? "text-[#1E293B]" : "text-slate-500"}`}>
              <span className={`rounded-full border-2 ${filter === p.id ? "border-[#F59E0B]" : "border-transparent"}`}><PetAvatar pet={p} size={56} /></span>{p.name}
            </button>
          ))}
        </div>
      )}

      {data.pets.length === 0 ? (
        <div className="pl-card p-8 text-center" data-testid="today-empty-pets">
          <p className="font-heading text-2xl font-bold">Add your first pet</p>
          <p className="text-slate-500 mt-1 mb-5">Then set up feeding, meds and walks.</p>
          <Link to="/app/pets/new" className="pl-btn-primary" data-testid="today-add-pet-btn"><Plus className="h-4 w-4" />Add pet</Link>
        </div>
      ) : tasks.length === 0 ? (
        <div className="pl-card p-8 text-center" data-testid="today-empty-tasks">
          <p className="font-heading text-2xl font-bold">Nothing scheduled today</p>
          <p className="text-slate-500 mt-1 mb-5">Add care routines on each pet's page.</p>
          <Link to="/app/pets" className="pl-btn-primary" data-testid="today-go-pets-btn">Set up routines</Link>
        </div>
      ) : (
        <>
          <div className="space-y-3" data-testid="today-todo-list">
            {todo.length === 0 && <div className="pl-card p-5 text-center bg-[#ECFDF5] border-[#BBF0D9] font-heading text-xl font-bold text-[#047857]" data-testid="today-all-done">All done for now. Good pack!</div>}
            {todo.map((t) => <TaskCard key={t.key} idx={data.tasks.indexOf(t)} task={t} onDone={done} busy={busy === t.key} />)}
          </div>
          {doneList.length > 0 && (
            <>
              <h2 className="font-heading text-lg font-semibold mt-8 mb-3 text-slate-500">Already done</h2>
              <div className="space-y-3" data-testid="today-done-list">
                {doneList.map((t) => <TaskCard key={t.key} idx={data.tasks.indexOf(t)} task={t} onUndo={undo} />)}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
